<template>
  <v-card class="header-container">
    <div class="header-title">
      <span class="header-offer-trade-title">Detalhes da campanha</span>
      <v-divider v-if="isMobile"></v-divider>
      <div v-if="offerSchedule.status === 'industry_proposal'" class="proposal-deadline-container" :class="{ 'proposal-deadline-warning': deadlineRunningOut }">
        <div class="proposal-deadline">
          <mf-icon v-if="deadlineRunningOut" small color="vermelho-0" fill icon="warning" />
          <mf-icon v-else fill small color="marca-0-l40">info</mf-icon>
          <span>Prazo de adesão:</span>
          <b>{{ getProposalDeadlineDate }}</b>
        </div>
        <template v-if="deadlineRunningOut">
          {{ isMobile ? '' : '| ' }}
          <b>{{ deadlineRunningOutMessage }}</b>
        </template>
      </div>
      <div v-else class="d-flex flex-row align-center">
        <div v-if="isSynchronizing" class="mr-4">
          <mf-icon fill size="12" color="#0079F2" icon="processing" class="mr-1" />
          <span class="sync-text">Sincronizando</span>
        </div>
        <offer-schedule-status :status="offerSchedule.status" />
      </div>
    </div>

    <v-divider v-if="!isMobile"></v-divider>

    <div v-if="!isMobile" class="terms-conditions-button">
      <terms-conditions-dialog
        id="terms-conditions"
        :banner="offerSchedule.industry.acceptance.image"
        :markdown-text="offerSchedule.industry.acceptance.terms"
      />
    </div>

    <div class="header-body">
      <div class="header-info-container">
        <!-- <div class="industry-image">
          <mf-icon xLarge color="cinza-l90">image</mf-icon>
        </div> -->
        <div class="general-info">
          <industry-chip v-if="!isMobile" :industry="offerSchedule.industry" />
          <span class="offer-schedule-name">{{ offerSchedule.name }}</span>
          <div class="chips-container">
            <div class="custom-chip rebate-chip">
              <div class="trending-up-container">
                <mf-icon color="white" size="16" icon="trending_up" fill />
              </div>
              <span>
                <b>{{ promotionText.bold }}</b>
                {{ promotionText.normal }}
              </span>
            </div>

            <industry-chip v-if="isMobile" :industry="offerSchedule.industry" />

            <div class="custom-chip">{{ getCampaignType }}</div>

            <div class="custom-chip">
              <span style="font-weight: normal">De </span>
              {{ getCampaignStartDate }}
              <span style="font-weight: normal"> até </span>
              {{ getCampaignEndDate }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="isMobile" class="mobile-terms-conditions-button">
        <terms-conditions-dialog
          id="terms-conditions"
          :banner="offerSchedule.industry.acceptance.image"
          :markdown-text="offerSchedule.industry.acceptance.terms"
        />
      </div>

      <div class="header-cards">
        <div class="card-item">
          <div class="card-item-title">
            <mf-icon fill color="cinza-d20" small>group</mf-icon>
            <span>Público alvo</span>
          </div>
          <span class="card-item-description">{{ getSegmentationLabel }}</span>
          <div v-if="getSegmentationType === 'massive'" class="card-item-chip-btn">
            <p class="info-description">Oferta disponível para todos os consumidores: clube e não clube.</p>
          </div>
          <div v-else-if="getSegmentationType === 'personalized'" class="card-item-chip-btn">
            <v-tooltip top :disabled="isActive">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <div id="industry_proposal_show_customers_count" :ripple="false" class="custom-chip chip-segment" @click="goToSegmentation(0)">
                    <span v-if="offerSchedule.segments[0]">
                      <b>{{ offerSchedule.segments[0].name }}</b>
                      ({{ offerSchedule.segments[0].clients_qty | zeroWhenEmpty | localeString }} clientes)
                    </span>
                  </div>
                </div>
              </template>
              <div class="custom-tooltip">
                <span class="description">Clique em cima do segmento e uma nova aba será aberta para visualizar os critérios da segmentação.</span>
              </div>
            </v-tooltip>
            <export-client-button
              id="industry_proposal_download_customers_button"
              :segment="offerSchedule.segments[0]"
              small
              color="marca-0-l40"
              icon="download"
              label="Baixar clientes"
            />
          </div>
          <div v-else>
            <p class="info-description">Sem segmentos selecionados.</p>
          </div>
        </div>

        <div class="card-item">
          <div class="card-item-title">
            <mf-icon fill color="cinza-d20" small>store</mf-icon>
            <span>Lojas participantes</span>
          </div>
          <span class="card-item-description">{{ infoCardUnitsDescription }}</span>
          <mf-select-units-dialog
            id="industry_proposal_edit_stores_button"
            template-type="button"
            btn-content="Alterar lojas"
            :disabled="includesThisStatus(['processing'])"
            :disable-save="includesThisStatus(['finished', 'expired', 'industry_rejected', 'processing']) || isDisabledInRules(rules.units)"
            :selected-units.sync="offerSchedule.units"
            :selected-units-configuration.sync="offerSchedule.units_configuration"
            :units="units"
            :unavailable-units="unavailableUnits"
            unavailable-tooltip="Esta loja não pode participar dessa oferta"
            required
            :tag-list="tagList"
            :default-data-type="defaultDataType"
            hide-ecommerce-configuration
          />
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import { DateTime } from 'luxon'
import { localeString } from '@/config/helpers.js'
import { getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import { mapGetters } from 'vuex'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { rules } from '@/modules/campanhas/gestao-ofertas-v2/helpers/rules.js'
import OfferScheduleStatus from './OfferScheduleStatus.vue'

export default {
  name: 'HeaderOfferTrade',
  components: {
    ExportClientButton: () => import('@/modules/clientes/segmentation/views/components/ExportClientButton.vue'),
    TermsConditionsDialog: () => import('@/modules/industria/ofertas-trade/views/components/offers-management/TermsConditionsDialog.vue'),
    IndustryChip: () => import('@/modules/industria/ofertas-trade/views/components/offer-schedule-list/IndustryChip.vue'),
    OfferScheduleStatus,
  },
  filters: {
    zeroWhenEmpty(value) {
      if (!value) return 0
      return value
    },
  },
  props: {
    offerSchedule: {
      type: Object,
      required: true,
    },
    isDisabledInRules: {
      type: Function,
      default: () => false,
    },
  },
  data: () => ({
    rules,
  }),
  computed: {
    ...mapGetters(['units', 'tagList']),
    unavailableUnits() {
      const allUnitsErp = this.units.map((u) => u.id_erp)
      return allUnitsErp.filter((unit) => !this.offerSchedule?.industry?.available_units?.includes(unit))
    },
    defaultDataType() {
      if (this.offerSchedule?.units_configuration?.length) {
        return this.offerSchedule?.units_configuration[0]?.dataType
      }
      return null
    },
    unitsError() {
      const errors = []
      if (!this.$v.offerSchedule.units.$dirty) return errors
      !this.$v.offerSchedule.units.required && errors.push('Selecione ao menos uma loja.')
      return errors
    },
    infoCardUnitsDescription() {
      const units = this.offerSchedule?.units?.length
      let text = 'loja selecionada'
      if (units) {
        if (units > 1) {
          text = 'lojas selecionadas'
        }
        return `${units} ${text}`
      }
      return 'Nenhuma loja'
    },
    getCampaignStartDate() {
      return DateTime.fromISO(this.offerSchedule.start_date).toFormat('dd/MM/yyyy')
    },
    getCampaignEndDate() {
      return DateTime.fromISO(this.offerSchedule.end_date).toFormat('dd/MM/yyyy')
    },
    getCampaignType() {
      return this.offerSchedule.types > 1 ? 'Múltiplos' : getProductFormatTypeLabel(this.offerSchedule.types[0])
    },
    getIndustryColor() {
      return this.offerSchedule?.industry?.color || '#00448c'
    },
    getProposalDeadlineDate() {
      return DateTime.fromISO(this.offerSchedule.industry.proposal_deadline).toFormat('dd/MM/yyyy - HH:mm')
    },
    promotionText() {
      if (this.offerSchedule.takepay_offer_sales?.length > 0) {
        const offers = this.offerSchedule.takepay_offer_sales[0]
        const diff = offers.take - offers.pay
        return {
          bold: diff === 1 ? `${diff} unidade será reembolsada` : `${diff} unidades serão reembolsadas`,
          normal: `a cada ${offers.take} vendidas!`,
        }
      } else if (this.offerSchedule.offer_sales?.length > 0) {
        const offers = this.offerSchedule.offer_sales[0]
        return {
          bold: `Você recebe R$ ${localeString(offers.rebate, 2)}`,
          normal: `a cada unidade vendida nessa oferta!`,
        }
      } else if (this.offerSchedule.percentage_offer_sales?.length > 0) {
        return {
          bold: 'Todo desconto concedido é pago pela indústria!',
          normal: '',
        }
      } else if (this.offerSchedule.next_buy_cashback_offer_sales?.length > 0 || this.offerSchedule.cashback_absolute_offer_sales?.length > 0) {
        return {
          bold: 'O Cashback será pago integralmente pela indústria!',
          normal: '',
        }
      } else if (this.offerSchedule.dynamic_rule_offer_sales.length > 0) {
        return {
          bold: 'O Voucher é adquirido e disparado pela indústria.',
          normal: '',
        }
      } else if (this.offerSchedule.cross_sell_offer_sales) {
        return {
          bold: 'Todo o desconto concedido é pago pela indústria!',
          normal: '',
        }
      }
      return { bold: '', normal: '' }
    },
    getSegmentationLabel() {
      switch (this.getSegmentationType) {
        case 'massive':
          return 'Massivo'
        case 'personalized':
          return 'Segmentado'
        case 'club':
          return 'Clube'
        default:
          return ''
      }
    },
    getSegmentationType() {
      return this.offerSchedule.industry.is_mercapromo ? 'massive' : this.offerSchedule.segments.length ? 'personalized' : 'club'
    },
    deadlineDifferenceInDays() {
      const proposalDeadline = this.offerSchedule?.industry?.proposal_deadline
      return Math.floor(DateTime.fromISO(proposalDeadline).diff(DateTime.now(), 'days').days)
    },
    deadlineRunningOut() {
      return this.deadlineDifferenceInDays <= 5
    },
    deadlineRunningOutMessage() {
      let message = ''
      if (this.deadlineDifferenceInDays === 1) {
        message = 'Resta 1 dia!'
      } else if (this.deadlineDifferenceInDays < 1) {
        return 'Termina hoje!'
      } else {
        message = `Restam ${this.deadlineDifferenceInDays} dias!`
      }
      return message
    },
    isSynchronizing() {
      return this.offerSchedule.external_synchronization?.status === 'SYNCHRONIZING' || this.offerSchedule.external_synchronization?.status === 'synchronizing'
    },
  },
  methods: {
    includesThisStatus(status) {
      return status.includes(this.offerSchedule.status)
    },
    formatDate(date) {
      return DateTime.fromISO(date).toFormat('dd/MM/yyyy')
    },
    goToSegmentation(index) {
      if (!this.offerSchedule.has_segment_data) {
        this.$snackbar({ message: 'Não é possível visualizar critérios desta campanha', snackbarColor: 'error' })
        return
      }

      const segment = this.offerSchedule.segments[index]

      if (!segment) return

      const routeData = this.$router.resolve({
        name: 'visualizar-segmentacao',
        params: {
          campaignId: this.offerSchedule._id,
          segmentId: segment._id,
        },
      })
      window.open(routeData.href, '_blank')
    },
  },
  validations: {
    offerSchedule: {
      name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(200),
        notOnlySpace,
      },
      units: {
        required,
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.terms-conditions-button {
  align-self: flex-end;
  margin-top: 22px;
  margin-right: 40px;
}

.mobile-terms-conditions-button {
  align-self: stretch;
}

.header-container {
  display: flex;
  flex-direction: column;
}

.header-title {
  display: flex;
  justify-content: space-between;
  padding: 24px 40px 16px 40px;
  align-items: center;

  .header-offer-trade-title {
    color: $cinza-d10;
    font-weight: bold;
    font-size: 1.25rem;

    @media screen and (max-width: $xs-breakpoint) {
      margin: 0px 16px;
    }
  }

  @media screen and (max-width: $xs-breakpoint) {
    flex-direction: column;
    padding: 16px 0px;
    gap: 16px;
    align-items: stretch;

    .title {
      margin: 0px 16px;
    }
  }
}

.proposal-deadline-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  padding: 10px 12px;
  border-radius: 8px;
  background-color: #d2e9ff80;

  color: #004d99;
  font-size: 14px;
  font-weight: 400;
  @media screen and (max-width: $xs-breakpoint) {
    margin: 0px 16px;
    flex-direction: column;
  }
}

.proposal-deadline {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.proposal-deadline-warning {
  background-color: #f47f6826;
  color: $vermelho-0;
}

.header-body {
  display: flex;
  flex-direction: column;
  gap: 48px;

  padding: 0px 40px 32px 40px;
  font-size: 14px;
  color: $cinza-d10;

  @media screen and (max-width: $xs-breakpoint) {
    padding: 0px 16px 16px 16px;
    gap: 24px;
  }

  .description {
    display: block;
    margin-bottom: 24px;
  }
}

.header-info-container {
  display: flex;
  flex-direction: row;
  gap: 48px;
}

.card-item {
  flex: 1;
  background-color: $cinza-l90;
  border-radius: 8px;
  padding: 16px 24px;

  display: flex;
  flex-direction: column;
  gap: 16px;

  .card-item-title {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    color: $cinza-d20;
    font-size: 14px;
  }

  .card-item-description {
    font-size: 16px;
    font-weight: 600;
    color: $marca-0-l10;
  }

  .card-item-chip-btn {
    display: flex;
    flex-direction: row;
    align-items: center;

    @media screen and (max-width: $xs-breakpoint) {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }
  }
}

.v-chip:not(.v-chip--active) {
  background: #fff !important;
}

.general-info {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media screen and (max-width: $xs-breakpoint) {
    align-items: center;
  }
}

.industry-image {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 120px;
  height: 120px;
  background-color: $cinza-l70;
  border-radius: 24px;
}

.header-cards {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: 2;
  gap: 24px;

  @media screen and (max-width: $xs-breakpoint) {
    flex-direction: column;
  }
}

.custom-chip {
  padding: 4px 16px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: bold;
  background-color: $cinza-l90;
  color: $cinza-d20;
  white-space: nowrap;
  width: fit-content;
  user-select: none;
}

.v-tooltip__content {
  background: #fff;
  border: 1px solid $cinza-l70;
  max-width: 350px;
  padding: 12px;
}

.custom-tooltip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  .description {
    color: $cinza-d10;
  }
}

.offer-schedule-name {
  color: $cinza-d10;
  font-size: 26px;
  font-weight: bold;

  @media screen and (max-width: $xs-breakpoint) {
    text-align: center;
  }
}

.chips-container {
  display: flex;
  flex-direction: row;
  gap: 32px;

  @media screen and (max-width: $xs-breakpoint) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
  }
}

.chip-segment {
  background-color: #fff;
  color: $cinza-d10;
  text-wrap: auto;
  font-weight: normal;
  padding: 10px 16px;
  border-radius: 20px;
}

.rebate-chip {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-direction: row;
  font-weight: 400;
  background-color: #0fc94d1a;
  color: $verde-0;

  @media screen and (max-width: $xs-breakpoint) {
    text-wrap: auto;
  }

  .trending-up-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background-color: $verde-l10;
  }
}

.sync-text {
  font-size: 14px;
  color: $cinza-0;
}
</style>
